import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { PageTitleService } from './page-title.service';

/**
 * Sets the browser tab title after every navigation.
 *
 * Angular's default strategy keeps the PARENT route's title whenever a child route has none, so every Admin,
 * Reports, Inventory or Consignment screen showed the same title. This strategy:
 *  - uses the deepest route that has a `title` (put one on every screen),
 *  - always sets something (never leaves the previous screen's title behind),
 *  - adds the " | LumberLinq" ending in one place,
 *  - applies the route's `data.description` (public pages) and `data.seo: 'index'` (public pages that may appear in search).
 *    Everything else is sent to search engines as noindex.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private readonly pageTitle = inject(PageTitleService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const description = this.deepestConfigData(snapshot.root, 'description');
    const seo = this.deepestConfigData(snapshot.root, 'seo');
    this.pageTitle.applyRoute(
      this.buildTitle(snapshot),
      typeof description === 'string' ? description : null,
      seo === 'index',
    );
  }

  /** Reads `data` exactly as written on the route configs (not the values inherited from parents), deepest one wins. */
  private deepestConfigData(route: ActivatedRouteSnapshot, key: string): unknown {
    let found: unknown = undefined;
    let current: ActivatedRouteSnapshot | undefined = route;
    while (current) {
      const value = (current.routeConfig?.data as Record<string, unknown> | undefined)?.[key];
      if (value !== undefined) {
        found = value;
      }
      current = current.children.find((child) => child.outlet === 'primary');
    }
    return found;
  }
}
